import { Typography } from '@mui/material'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { VesselVisitBulkEditFormStoreType } from '../../Stores/VesselVisitBulkEditFormStore'

type Props = {
  data: VesselVisitBulkEditFormStoreType
}

const fieldDefinitions = [
  {
    labelKey: 'inboundVisit',
    labelDefault: 'Inbound visit',
    getValue: (data: Props['data']) => data.inboundTransport.carrierVisit,
    format: (value: any) => `${value?.identifier} - ${formatDateTime(value?.eta)}`,
  },
  {
    labelKey: 'inboundCarrierType',
    labelDefault: 'Inbound carrier type',
    getValue: (data: Props['data']) => data.inboundTransport.carrierType,
  },
  {
    labelKey: 'placeOfOrigin',
    labelDefault: 'Place of origin',
    getValue: (data: Props['data']) => data.inboundTransport.origin,
  },
  {
    labelKey: 'approvalForDischarge',
    labelDefault: 'Approval for discharge',
    getValue: (data: Props['data']) => data.inboundTransport.approvalForDischarge,
    format: (value: any) => (value ? 'Yes' : 'No'),
  },

  {
    labelKey: 'outboundVisit',
    labelDefault: 'Outbound visit',
    getValue: (data: Props['data']) => data.outboundTransport.carrierVisit,
    format: (value: any) => `${value?.identifier} - ${formatDateTime(value?.eta)}`,
  },
  {
    labelKey: 'outboundCarrierType',
    labelDefault: 'Outbound carrier type',
    getValue: (data: Props['data']) => data.outboundTransport.carrierType,
  },
  {
    labelKey: 'finalDestination',
    labelDefault: 'Final destination',
    getValue: (data: Props['data']) => data.outboundTransport.finalDestination,
  },
  {
    labelKey: 'portOfDischarge',
    labelDefault: 'Port of discharge',
    getValue: (data: Props['data']) => data.outboundTransport.portOfDischarge,
  },
  {
    labelKey: 'portOfLoading',
    labelDefault: 'Port of loading',
    getValue: (data: Props['data']) => data.inboundTransport.portOfLoading,
  },
  {
    labelKey: 'inlandDestination',
    labelDefault: 'Inland destination',
    getValue: (data: Props['data']) => data.outboundTransport.inlandDestination,
  },
  {
    labelKey: 'approvalForLoading',
    labelDefault: 'Approval for loading',
    getValue: (data: Props['data']) => data.outboundTransport.approvalForLoading,
    format: (value: any) => (value ? 'Yes' : 'No'),
  },
  {
    labelKey: 'stowageInstructions',
    labelDefault: 'Stowage instructions',
    getValue: (data: Props['data']) => data.outboundTransport.stowageInstructions?.name,
  },

  {
    labelKey: 'consignee',
    labelDefault: 'Consignee',
    getValue: (data: Props['data']) => data.logistics.consignee,
  },
  {
    labelKey: 'forwarder',
    labelDefault: 'Forwarder',
    getValue: (data: Props['data']) => data.logistics.forwarder,
    format: (value: any) => value?.name,
  },
  {
    labelKey: 'shipper',
    labelDefault: 'Shipper',
    getValue: (data: Props['data']) => data.logistics.shipper,
    format: (value: any) => value?.name,
  },
  {
    labelKey: 'billOfLadding',
    labelDefault: 'Bill of lading',
    getValue: (data: Props['data']) => data.logistics.billOfLadding,
  },
  {
    labelKey: 'bookingReference',
    labelDefault: 'Booking reference',
    getValue: (data: Props['data']) => data.logistics.bookingReference,
  },
  {
    labelKey: 'cargoStatus',
    labelDefault: 'Cargo status',
    getValue: (data: Props['data']) => data.cargo.cargoStatus,
  },
  {
    labelKey: 'commodity',
    labelDefault: 'Commodity',
    getValue: (data: Props['data']) => data.cargo.commodity,
  },
]

export const ChangesSummary: React.FC<Props> = observer(({ data }) => {
  const { t } = useTranslate()

  const changedFields = fieldDefinitions.filter(field => field.getValue(data) !== undefined)

  return (
    <>
      <Typography variant='subtitle2' sx={{ mt: 2 }}>
        {t('summaryOfChanges', 'Summary of changes:')}
      </Typography>

      {changedFields.length === 0 ? (
        <Typography variant='body2'>{t('noChangesMade', 'No changes made.')}</Typography>
      ) : (
        changedFields.map(field => {
          const value = field.getValue(data)

          if (value === undefined) {
            return null
          }

          if (value === null) {
            return (
              <Typography key={field.labelKey} variant='body2' sx={{ pl: 1 }}>
                {t(field.labelKey, field.labelDefault)}: <strong>{t('cleared', 'Clear')}</strong>
              </Typography>
            )
          }

          const formattedValue = field.format ? field.format(value) : String(value)

          return (
            <Typography key={field.labelKey} variant='body2' sx={{ pl: 1 }}>
              {t(field.labelKey, field.labelDefault)}: <strong>{formattedValue}</strong>
            </Typography>
          )
        })
      )}
    </>
  )
})
